import { Store } from 'redux';
import { Action, History, Location, Update } from 'history';
import { IHref, createHref } from 'components/Shared/server';
import { historyChangeLocation } from 'store/history/actions';
import { ILocation } from 'models/ILocation';
import { getPage, updateCurrentPage } from 'components/Pagelayout/actions';
import batchAction from 'store/batching';
import { HistoryState } from './types';
import { historyLocationState } from './historyLocationState';

interface StateLocation {
  location?: ILocation;
  currentPage?: {
    componentName?: string;
  };
}

interface GetShouldFetchPageProps {
  currentLocation: StateLocation;
  nextLocation: Pick<ILocation, 'pathname'>;
}
const getShouldFetchPage = ({ currentLocation, nextLocation }: GetShouldFetchPageProps) => {
  const componentName = currentLocation?.currentPage?.componentName;
  const isProductlistPage =
    componentName === 'SearchProductlistPage' ||
    componentName === 'ProductlistPage' ||
    componentName === 'EditorialPage';
  const isSameLocation =
    currentLocation?.location && currentLocation.location.pathname !== nextLocation.pathname;
  return isProductlistPage || isSameLocation;
};

export const historySync = <S extends StateLocation, H extends HistoryState = HistoryState>(
  history: History,
  store: Store<S>,
  w: Window
): History & { unsubscribe: () => void } => {
  if ('scrollRestoration' in w.history) {
    w.history.scrollRestoration = 'manual';
  }
  let nextLocation: IHref;
  const locationToHref = (location: Location) => createHref(history.createHref(location));
  const dispatchChangeLocation = (location: Location, action?: Action) =>
    store.dispatch(
      historyChangeLocation({
        url: locationToHref(location),
        action
      })
    );
  dispatchChangeLocation(history.location);

  const handleLocationChange = ({ location, action }: Update) => {
    nextLocation = locationToHref(location);
    const currentLocation = store.getState();
    const shouldFetchPage = getShouldFetchPage({ currentLocation, nextLocation });
    const isHashChanged = nextLocation.hash && nextLocation.hash !== currentLocation.location?.hash;

    if (action !== 'POP' || !shouldFetchPage || isHashChanged) {
      dispatchChangeLocation(location, action);
      return;
    }
    const state = historyLocationState<H>(history);
    const scrollTo: [number, number] = state?.scrollTo ?? [0, 0];
    const screenHeight: number = state?.screenHeight ?? 0;
    store.dispatch(
      batchAction(
        state?.page && updateCurrentPage(state.page),
        getPage(nextLocation, { emit: false, scrollTo, screenHeight }),
        dispatchChangeLocation(location, action)
      )
    );
  };
  const unsubscribe = history.listen(handleLocationChange);

  return {
    ...history,
    unsubscribe
  };
};
